import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  Building2,
  Users as UsersIcon,
  DollarSign,
  Inbox,
  Megaphone,
  ServerCog,
  ScrollText,
  Mail,
  Contact2,
  Trophy,
  LayoutTemplate,
  ShieldCheck,
  LogOut,
  X,
  ChevronUp,
  Gift,
} from "lucide-react";
import { useAuthStore } from "../../store/authStore";
import { SerpixWordmark } from "../../components/brand/SerpixWordmark";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

const NavItem = ({ to, icon: Icon, label, testId, end }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={testId}
    className={({ isActive }) =>
      [
        "flex items-center gap-2.5 rounded-md px-3 py-1.5 text-[13px] transition-colors",
        isActive
          ? "bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-md shadow-violet-500/20"
          : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900",
      ].join(" ")
    }
  >
    <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
    <span className="truncate">{label}</span>
  </NavLink>
);

export default function PlatformSidebar({ open = false, onClose }) {
  const { t, i18n } = useTranslation();
  const { user, logout, setLanguage } = useAuthStore();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const toggleLang = () => setLanguage(i18n.language === "tr" ? "en" : "tr");

  return (
    <aside
      className={[
        "fixed left-0 top-0 z-40 flex h-[100dvh] w-72 max-w-[85vw] flex-col border-r border-zinc-200 bg-white p-5 pb-[max(1rem,env(safe-area-inset-bottom))] transition-transform duration-300 md:h-screen md:w-64 md:max-w-none md:translate-x-0 md:pb-5",
        open ? "translate-x-0 shadow-2xl" : "-translate-x-full",
      ].join(" ")}
      style={{ paddingTop: "max(1.25rem, env(safe-area-inset-top))" }}
      data-testid="platform-sidebar"
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SerpixWordmark size="sm" />
          <span
            data-testid="platform-badge"
            className="rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white shadow-sm"
          >
            Admin
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="platform-sidebar-close-btn"
          aria-label="Close menu"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-zinc-200 text-zinc-500 transition-colors hover:border-zinc-300 hover:text-zinc-900 md:hidden"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mb-3 rounded-xl border border-violet-100 bg-gradient-to-br from-violet-50 to-indigo-50 px-3 py-2 text-[10px] text-violet-900">
        <div className="flex items-center gap-1.5 font-semibold uppercase tracking-wider">
          <ShieldCheck className="h-3 w-3" /> {t("platform.nav.control")}
        </div>
        <p className="mt-0.5 text-[9px] leading-relaxed text-violet-800/80">
          {t("platform.nav.tagline")}
        </p>
      </div>

      <nav className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto">
        <NavItem
          to="/app/platform"
          icon={LayoutDashboard}
          label={t("platform.nav.overview")}
          testId="platform-nav-overview"
          end
        />
        <NavItem
          to="/app/platform/tenants"
          icon={Building2}
          label={t("platform.nav.tenants")}
          testId="platform-nav-tenants"
        />
        <NavItem
          to="/app/platform/invitations"
          icon={Gift}
          label={t("platform.nav.invitations")}
          testId="platform-nav-invitations"
        />
        <NavItem
          to="/app/platform/users"
          icon={UsersIcon}
          label={t("platform.nav.users")}
          testId="platform-nav-users"
        />
        <NavItem
          to="/app/platform/billing"
          icon={DollarSign}
          label={t("platform.nav.billing")}
          testId="platform-nav-billing"
        />
        <NavItem
          to="/app/platform/outbox"
          icon={Inbox}
          label={t("platform.nav.outbox")}
          testId="platform-nav-outbox"
        />
        <NavItem
          to="/app/platform/announcements"
          icon={Megaphone}
          label={t("platform.nav.announcements")}
          testId="platform-nav-announcements"
        />
        <NavItem
          to="/app/platform/system"
          icon={ServerCog}
          label={t("platform.nav.system")}
          testId="platform-nav-system"
        />
        <NavItem
          to="/app/platform/audit"
          icon={ScrollText}
          label={t("platform.nav.audit")}
          testId="platform-nav-audit"
        />
        <NavItem
          to="/app/platform/digest"
          icon={Mail}
          label={t("platform.nav.digest")}
          testId="platform-nav-digest"
        />
        <NavItem
          to="/app/platform/contact-statuses"
          icon={Contact2}
          label="Kontak Statüleri"
          testId="platform-nav-contact-statuses"
        />
        <NavItem
          to="/app/platform/deal-stages"
          icon={Trophy}
          label="Fırsat Aşamaları"
          testId="platform-nav-deal-stages"
        />
        <NavItem
          to="/app/platform/templates"
          icon={LayoutTemplate}
          label="Şablonlar"
          testId="platform-nav-templates"
        />
      </nav>

      <div className="mt-3 border-t border-zinc-200 pt-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="platform-user-menu"
              className="group flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-zinc-100"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 text-xs font-semibold text-white shadow-md shadow-violet-500/30">
                {(user?.name || "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-zinc-900">
                  {user?.name || "Platform Admin"}
                </div>
                <div className="truncate text-[11px] text-zinc-500">
                  {user?.email}
                </div>
              </div>
              <ChevronUp className="h-4 w-4 text-zinc-400" strokeWidth={1.75} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            side="top"
            className="w-56"
            data-testid="platform-user-menu-content"
          >
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-zinc-500">
              Platform Admin
            </DropdownMenuLabel>
            <DropdownMenuItem onClick={toggleLang} className="cursor-pointer">
              {i18n.language === "tr" ? "English" : "Türkçe"}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onLogout}
              data-testid="platform-logout-btn"
              className="cursor-pointer text-zinc-700"
            >
              <LogOut className="mr-2 h-4 w-4" strokeWidth={1.75} />
              {t("auth.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}
